import { useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AxiosError } from 'axios';
import {
  Tag,
  MessageSquare,
  Info,
  FileText,
  Share2,
  Link2,
  Edit2,
  Trash2,
  MoreVertical,
  Download,
  Home,
} from 'lucide-react';
import {
  useFile,
  useVideoPlayer,
  useDownload,
  useDeleteFile,
} from '@features/files';
import { FileViewerPanel } from '@features/files/components/FileViewerPanel';
import { inferFileKind } from '@common/utils/fileKind';
import { useBreadcrumb } from '@features/folders';
import { ShareModal } from '@features/shares';
import {
  ActionMenu,
  Badge,
  Breadcrumb,
  Button,
  Card,
  IconButton,
  Spinner,
  Tabs,
  Tooltip,
  useToast,
} from '@ntconsult/aluma-react';
import { ConfirmDialog } from '@common/components/ConfirmDialog';
import { useDeleteConfirmation } from '@common/hooks/useDeleteConfirmation';
import { VideoErrorState } from '@features/files/components/video-detail/VideoErrorState';
import { VideoInfoTab } from '@features/files/components/video-detail/VideoInfoTab';
import { VideoCommentsTab } from '@features/files/components/video-detail/VideoCommentsTab';
import { VideoTranscriptionTab } from '@features/files/components/video-detail/VideoTranscriptionTab';
import { EditMetadataDrawer } from '@features/files/components/video-detail/EditMetadataDrawer';
import { useFileComments } from '@features/files/hooks/useFileComments';
import { useCanEditFile } from '@features/files/hooks/useCanEditFile';

export function VideoPage() {
  const { fileId } = useParams<{ fileId: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation('video');
  const { toast } = useToast();

  const { data: file, isLoading, error, refetch, isRefetching } = useFile(fileId);
  const { data: breadcrumbItems } = useBreadcrumb(file?.folderId);
  const { data: comments = [] } = useFileComments(fileId ?? '');
  const canEdit = useCanEditFile(file);

  const { mutate: download } = useDownload();
  const { mutate: deleteFile } = useDeleteFile();
  const deleteConfirmation = useDeleteConfirmation();

  const [editOpen, setEditOpen] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-24">
        <Spinner size="lg" />
      </div>
    );
  }

  if (!file) {
    const status = (error as AxiosError | null)?.response?.status;
    return (
      <VideoErrorState
        status={status}
        isRefetching={isRefetching}
        onRetry={() => refetch()}
      />
    );
  }

  return (
    <VideoPageContent
      file={file}
      breadcrumbItems={breadcrumbItems}
      comments={comments}
      canEdit={canEdit}
      editOpen={editOpen}
      setEditOpen={setEditOpen}
      shareOpen={shareOpen}
      setShareOpen={setShareOpen}
      onDownload={() => download(file.id)}
      onCopyLink={() => {
        const url = `${window.location.origin}/files/view/${file.id}`;
        navigator.clipboard.writeText(url).then(() => {
          toast.success({ title: t('actions.linkCopied') });
        });
      }}
      onRequestDelete={() => {
        deleteConfirmation.requestDelete(file.name, () =>
          deleteFile(file.id, {
            onSuccess: () => navigate('/files'),
          }),
        );
      }}
      deleteConfirmation={deleteConfirmation}
      t={t}
    />
  );
}

interface VideoPageContentProps {
  file: NonNullable<ReturnType<typeof useFile>['data']>;
  breadcrumbItems: ReturnType<typeof useBreadcrumb>['data'];
  comments: ReturnType<typeof useFileComments>['data'];
  canEdit: boolean;
  editOpen: boolean;
  setEditOpen: (v: boolean) => void;
  shareOpen: boolean;
  setShareOpen: (v: boolean) => void;
  onDownload: () => void;
  onCopyLink: () => void;
  onRequestDelete: () => void;
  deleteConfirmation: ReturnType<typeof useDeleteConfirmation>;
  t: (key: string, vars?: Record<string, unknown>) => string;
}

type TabId = 'info' | 'comments' | 'transcription';

function VideoPageContent({
  file,
  breadcrumbItems,
  comments,
  canEdit,
  editOpen,
  setEditOpen,
  shareOpen,
  setShareOpen,
  onDownload,
  onCopyLink,
  onRequestDelete,
  deleteConfirmation,
  t,
}: VideoPageContentProps) {
  const player = useVideoPlayer(file);
  const [activeTab, setActiveTab] = useState<TabId>('info');
  const commentCount = comments?.length ?? 0;
  const isVideo = inferFileKind(file.mimeType, file.name) === 'video';

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <Breadcrumb>
          <Breadcrumb.List>
            <Breadcrumb.Item>
              <Breadcrumb.Link asChild>
                <Link to="/files">
                  <Home className="w-4 h-4" aria-hidden="true" />
                </Link>
              </Breadcrumb.Link>
            </Breadcrumb.Item>
            {breadcrumbItems?.map((item) => (
              <Breadcrumb.Item key={item.id}>
                <Breadcrumb.Separator />
                <Breadcrumb.Link asChild>
                  <Link to={`/files/${item.id}`}>{item.name}</Link>
                </Breadcrumb.Link>
              </Breadcrumb.Item>
            ))}
            <Breadcrumb.Item>
              <Breadcrumb.Separator />
              <Breadcrumb.Page>{file.name}</Breadcrumb.Page>
            </Breadcrumb.Item>
          </Breadcrumb.List>
        </Breadcrumb>

        <div className="flex items-center gap-1">
          <Tooltip content={t('player.download')}>
            <IconButton
              variant="ghost"
              size="sm"
              label={t('player.download')}
              icon={<Download className="w-4 h-4" aria-hidden="true" />}
              onClick={onDownload}
            />
          </Tooltip>
          <Tooltip content={t('actions.copyLink')}>
            <IconButton
              variant="ghost"
              size="sm"
              label={t('actions.copyLink')}
              icon={<Link2 className="w-4 h-4" aria-hidden="true" />}
              onClick={onCopyLink}
            />
          </Tooltip>
          <Button
            variant="secondary"
            size="sm"
            onClick={() => setShareOpen(true)}
            leftIcon={<Share2 className="w-4 h-4" aria-hidden="true" />}
            aria-label={t('actions.share')}
          >
            <span className="hidden sm:inline">{t('actions.share')}</span>
          </Button>
          {canEdit && (
            <ActionMenu
              align="right"
              trigger={
                <IconButton
                  variant="ghost"
                  size="sm"
                  label={t('actions.more')}
                  icon={<MoreVertical className="w-4 h-4" aria-hidden="true" />}
                />
              }
            >
              <ActionMenu.Item
                icon={<Edit2 className="w-4 h-4" aria-hidden="true" />}
                onClick={() => setEditOpen(true)}
              >
                {t('edit.button')}
              </ActionMenu.Item>
              <ActionMenu.Separator />
              <ActionMenu.Item
                variant="danger"
                icon={<Trash2 className="w-4 h-4" aria-hidden="true" />}
                onClick={onRequestDelete}
              >
                {t('actions.delete')}
              </ActionMenu.Item>
            </ActionMenu>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_380px] gap-4 lg:gap-6 items-start">
        <div className="space-y-4 min-w-0">
          <FileViewerPanel file={file} player={player} onDownload={onDownload} />

          <div className="space-y-2">
            <h1 className="text-lg sm:text-xl font-bold text-foreground leading-tight break-words">
              {file.name}
            </h1>
            {file.tags && file.tags.length > 0 && (
              <div className="flex flex-wrap items-center gap-1.5">
                <Tag className="w-3.5 h-3.5 text-foreground-subtle" aria-hidden="true" />
                {file.tags.map((tag) => (
                  <Badge key={tag} variant="soft" tone="brand" shape="pill" size="sm">
                    {tag}
                  </Badge>
                ))}
              </div>
            )}
          </div>
        </div>

        <Card padding="none" className="lg:sticky lg:top-4">
          <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as TabId)}>
            <Tabs.List className="border-b border-border">
              <Tabs.Trigger value="info" className="flex-1 gap-1.5">
                <Info className="w-3.5 h-3.5" aria-hidden="true" />
                {t('tabs.info')}
              </Tabs.Trigger>
              <Tabs.Trigger value="comments" className="flex-1 gap-1.5">
                <MessageSquare className="w-3.5 h-3.5" aria-hidden="true" />
                {t('tabs.comments')}
                {commentCount > 0 && (
                  <Badge variant="soft" tone="neutral" size="sm">
                    {commentCount}
                  </Badge>
                )}
              </Tabs.Trigger>
              {isVideo && (
                <Tabs.Trigger value="transcription" className="flex-1 gap-1.5">
                  <FileText className="w-3.5 h-3.5" aria-hidden="true" />
                  {t('tabs.transcription')}
                </Tabs.Trigger>
              )}
            </Tabs.List>

            <Tabs.Content value="info" className="p-4">
              <VideoInfoTab file={file} onEdit={() => setEditOpen(true)} />
            </Tabs.Content>
            <Tabs.Content value="comments" className="p-4">
              <VideoCommentsTab
                fileId={file.id}
                currentTime={player.state.currentTime}
                onSeek={player.actions.seekTo}
              />
            </Tabs.Content>
            {isVideo && (
              <Tabs.Content value="transcription" className="p-4">
                <VideoTranscriptionTab />
              </Tabs.Content>
            )}
          </Tabs>
        </Card>
      </div>

      <EditMetadataDrawer file={file} open={editOpen} onClose={() => setEditOpen(false)} />

      {shareOpen && (
        <ShareModal
          isOpen
          onClose={() => setShareOpen(false)}
          resourceType="file"
          resourceId={file.id}
          resourceName={file.name}
        />
      )}

      <ConfirmDialog
        open={deleteConfirmation.isOpen}
        title={t('actions.delete')}
        description={deleteConfirmation.pending?.name ?? ''}
        confirmLabel={t('actions.delete')}
        onConfirm={deleteConfirmation.confirm}
        onCancel={deleteConfirmation.cancel}
      />
    </div>
  );
}
